import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import { i18n } from "@/i18n";
import { teamAPI } from "@/api";
import ColorsMixin from "@/components/ColorsMixin";
import getDiscProfileDescriptions from "@/helpers/getDiscProfileDescriptions";
import TeamRoleType from "@/constants/TeamRoleType";
import TeamType from "@/constants/TeamType";
import { initWebSocketListeners } from "./teamApiOrchestrator";

initWebSocketListeners();

import {
	TeamNameUpdatedEvent
} from "@/telemetry";

function formatForTeamPage( team ) {
	const leadCount = team.members.reduce( ( accum, m ) => {
		if ( m.role === TeamRoleType.LEAD && m.status === "ACTIVE" ) {
			accum += 1;
		}
		return accum;
	}, 0 );
	const { isFavorited, } = team;
	return {
		id: team.id,
		name: team.name,
		members: team.members.map( member => {
			const typeColor = ColorsMixin.computed[ `disc${member.assessments?.DISC?.moniker}` ];
			return {
				personId: member.personId,
				name: member.name,
				email: member.email,
				picture: member.picture,
				roleValue: member.role,
				isLead: member.role === TeamRoleType.LEAD,
				isActive: member.status === "ACTIVE",
				isOnlyTeamLead: ( leadCount === 1 && member.role === TeamRoleType.LEAD && member.status === "ACTIVE" ),
				roleLabel: i18n.t( `teamManagement.TeamEditPage_${ member.role }_roleLabelText` ),
				statusValue: member.status,
				statusLabel: i18n.t( `teamManagement.TeamEditPage_${ member.status }_statusLabelText` ),
				workStyle: member.assessments?.DISC?.moniker,
				typeColor: typeColor && typeColor(),
				isRegisteredUser: member.isRegisteredUser,
				created: member.created,
				assigned: member.assigned,
			};
		} ),
		isFavorited,
	};
}

const _state = {
	myTeams: [],
	activeTeam: null,
	isLoadingTeam: false,
	isSavingTeam: false,
	isChangingFavoriteStatus: false,
	selectedTeamMember: undefined,
	selectedTeamEditTab: {},
	tabs: [
		{
			key: TeamType.TEAM,
			title: i18n.t( "teamManagement.Teams_teamTab_label" ),
		},
		{
			key: TeamType.ONE_ON_ONE,
			title: i18n.t( "teamManagement.Teams_oneOnOneTab_label" ),
		},
	],
};

const _getters = {
	member: ( state, getters ) => personId => {
		return getters.activeTeam.members.find( member => member.personId === personId );
	},
	activeTeam( state ) {
		return state.activeTeam;
	},
	isSavingTeam: state => state.isSavingTeam,
	isChangingFavoriteStatus: state => state.isChangingFavoriteStatus,
	selectedTeamMember: state => state.selectedTeamMember,
	selectedTeamEditTab: state => state.selectedTeamEditTab,
};

const actions = {
	async createOneOnOneInBackground( { dispatch, }, {
		managerEmail,
		managerName,
		isInitiatorLead,
		reportEmail,
		reportRole,
		relatedTeamId,
	} ) {
		try {
			Nprogress.start();
			await teamAPI.createOneOnOne( {
				managerEmail,
				managerName,
				isInitiatorLead,
				reportEmail,
				reportRole,
				relatedTeamId,
			} );
			await Promise.all( [
				dispatch( "loadTeams", {
					force: true,
				}, { root: true, } ),
				dispatch( "loadTeam", relatedTeamId ),
			] );
		} catch ( e ) {
			VueNotifications.error( {
				message: i18n.t( "teamManagement.TeamInvite_OneOnOneErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
	},
	async inviteTeamMember( { commit, state, }, { email, teamId, } ) {
		try {
			Nprogress.start();
			await teamAPI.inviteTeamMember( { teamId, email, } );
			const team = await teamAPI.fetchTeam( { teamId, } );
			commit( "loadedTeam", team );
			if ( state.activeTeam && team.id === state.activeTeam.id ) {
				commit( "activeTeamUpdated", team );
			}
			VueNotifications.success( {
				message: i18n.t( "teamManagement.TeamInvite_SuccessMessage", { email, } ),
			} );
		} catch ( e ) {
			VueNotifications.error( {
				message: i18n.t( "teamManagement.TeamInvite_ErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
	},
	async loadTeam( { commit, }, teamId ) {
		let wasSuccessful = true;
		commit( "toggleLoadingTeam", true );
		try {
			Nprogress.start();
			const team = await teamAPI.fetchTeam( { teamId, } );
			commit( "loadedTeam", team );
		} catch ( e ) {
			wasSuccessful = false;
			VueNotifications.error( {
				message: i18n.t( "teamManagement.TeamEditPage_loading_ErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		commit( "toggleLoadingTeam", false );
		Nprogress.done();
		return wasSuccessful;
	},
	async removeTeamMember( { commit, rootState, }, { teamId, personId, } ) {
		try {
			Nprogress.start();
			await teamAPI.removeTeamMember( { teamId, personId, } );

			if ( rootState.auth.user.personId !== personId ) {
				commit( "removedTeamMember", { teamId, personId, } );
				const team = await teamAPI.fetchTeam( { teamId, } );
				commit( "loadedTeam", team );
			} else {
				// invoke mutation in global team store
				commit( "removedMyselfFromTeam", { teamId, }, { root: true, } );
				// invoke mutation in THIS store module
				commit( "removedMyselfFromTeam", { teamId, } );
			}

			VueNotifications.success( {
				message: i18n.t( "teamManagement.TeamEditPage_removingMember_SuccessMessage" ),
			} );
		} catch ( e ) {
			VueNotifications.error( {
				message: i18n.t( "teamManagement.TeamEditPage_removingMember_ErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
	},
	async updateTeamMemberRole( { commit, }, { selectedRole, personId, teamId, } ) {
		try {
			Nprogress.start();
			await teamAPI.updateTeamMember( { teamId, personId, role: selectedRole, } );
			const team = await teamAPI.fetchTeam( { teamId, } );
			commit( "loadedTeam", team );
			VueNotifications.success( {
				message: i18n.t( "teamManagement.TeamEditPage_updatingMember_SuccessMessage" ),
			} );
		} catch ( e ) {
			VueNotifications.error( {
				message: i18n.t( "teamManagement.TeamEditPage_updatingMember_ErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
	},
	async updateTeamName( { commit, }, { teamId, teamName, } ) {
		commit( "toggleSavingTeam", true );
		try {
			Nprogress.start();
			await teamAPI.saveTeam( { teamId, name: teamName, } );
			commit( "teamNameChanged", { teamId, teamName, } );
			const team = await teamAPI.fetchTeam( { teamId, } );
			commit( "loadedTeam", team );

			const telemetryEvent = new TeamNameUpdatedEvent();
			telemetryEvent.submit();

			VueNotifications.success( {
				message: i18n.t( "teamManagement.TeamEditPage_updatingTeamName_SuccessMessage" ),
			} );
		} catch ( e ) {
			VueNotifications.error( {
				message: i18n.t( "teamManagement.TeamEditPage_updatingTeamName_ErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
		commit( "toggleSavingTeam", false );
	},
	changeFavoriteStatus: async ( { commit, /* state, dispatch, rootGetters, rootState, */ }, { teamId, isFavorite, } ) => {
		try {
			Nprogress.start();
			commit( "toggleChangingFavoriteStatus", true );
			if ( isFavorite ) {
				await teamAPI.favoriteTeam( { teamId, } );
			} else {
				await teamAPI.unfavoriteTeam( { teamId, } );
			}
		} catch ( e ) {
			const messageKey = isFavorite ?
				"teamManagement.TeamGrid_errorFavoritingTeam" :
				"teamManagement.TeamGrid_errorUnfavoritingTeam";
			const message = i18n.t( messageKey, {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			Nprogress.done();
			commit( "toggleChangingFavoriteStatus", false );
		}
	},
};

/**
 * Reactions are really just mutations that are called when an
 *   action invokes the store's `commit()` function. Separating them
 *   here for clarity. They are merged into mutations below.
 */
const reactions = {
	loadedTeam( state, team ) {
		const otherTeams = state.myTeams.filter( t => t.id !== team.id );
		state.myTeams = [ ...otherTeams, team, ];
		state.activeTeam = formatForTeamPage( team );
	},
	removedTeamMember( state, { teamId, personId, } ) {
		const team = state.myTeams.find( t => t.id === teamId );
		const memberIndex = team.members.findIndex( m => m.personId === personId );
		if ( memberIndex >= 0 ) {
			team.members.splice( memberIndex, 1 );
		}
	},
	removedMyselfFromTeam( state, { teamId, } ) {
		state.myTeams = state.myTeams.filter( myTeam => myTeam.id !== teamId );
		state.activeTeam = null;
	},
	teamNameChanged( state, { teamId, teamName, } ) {
		const team = state.myTeams.find( t => t.id === teamId );
		team.name = teamName;
	},
	toggleSavingTeam( state, isSaving ) {
		state.isSavingTeam = isSaving;
	},
	teamUpdated( state, team ) {
		const otherTeams = state.myTeams.filter( t => t.id !== team.id );
		state.myTeams = [ ...otherTeams, team, ];
		if ( team.id === state.activeTeam?.id ) {
			state.activeTeam = formatForTeamPage( team );
		}
	},
	activeTeamUpdated( state, activeTeam ) {
		state.activeTeam = formatForTeamPage( activeTeam );
	},
};

const mutations = {
	...reactions,
	loadedTeams( state, loadedTeams ) {
		state.myTeams = loadedTeams;
	},
	setActiveTeamId( state, activeTeamId ) {
		const myTeam = state.myTeams.find( t => t.id === activeTeamId );
		state.activeTeam = myTeam ? formatForTeamPage( myTeam ) : null;
		state.selectedTeamMember = undefined;
	},
	toggleLoadingTeam( state, isLoadingTeam ) {
		state.isLoadingTeam = isLoadingTeam;
	},
	toggleChangingFavoriteStatus( state, isChangingFavoriteStatus ) {
		state.isChangingFavoriteStatus = isChangingFavoriteStatus;
	},
	selectTeamMember( state, { teamId, personId, } ) {
		const team = state.myTeams.find( t => t.id === teamId );
		const member = team.members.find( m => m.personId === personId );
		const selectedMember = {
			personId: member.personId,
			name: member.name,
			email: member.email,
			picture: member.picture,
			preferredName: member.preferredName,
			assessment: member.assessments?.DISC,
		};
		if ( selectedMember.assessment ) {
			selectedMember.discWorkStyleDescriptions = getDiscProfileDescriptions( {
				assessment: selectedMember.assessment,
				person: member.preferredName || member.name.split( " " )[ 0 ],
			} );
		}
		state.selectedTeamMember = selectedMember;
	},
	clearSelectedTeamMember( state ) {
		state.selectedTeamMember = undefined;
	},
	selectTeamEditTab: ( state, tab ) => {
		state.selectedTeamEditTab = tab;
	},
};

export default {
	state: _state,
	getters: _getters,
	actions,
	mutations,
	namespaced: true,
};



// WEBPACK FOOTER //
// ./src/store/modules/teamManagement/index.js